import _ from 'lodash';
import moment from 'moment';
import { TYPE_BEAT_ENUM, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';

/**
 * Estimates the byte size of an ECG segment spanning two timestamps. Multiplies the coerced numeric difference by the per-second byte rate.
 *
 * @param stop - End of the segment.
 * @param start - Start of the segment.
 * @param bytesPerSecond - Bytes the recording occupies per second.
 * @returns The estimated size in bytes.
 */
const getSizeData = (stop: number, start: number, bytesPerSecond: number) => {
  const diffTime = +stop - +start;
  const sizeData = diffTime * bytesPerSecond;
  return sizeData;
};

const addDataRanges = ({
  summaries,
  start: startEvent,
  stop: stopEvent,
  samplingFrequency,
  channels,
}: {
  summaries: any[];
  start: number;
  stop: number;
  samplingFrequency: number;
  channels: any[];
}) => {
  const bytesPerSample = 2;
  const samplePerMsSecond = (channels.length * bytesPerSample * samplingFrequency) / 1000;
  const lenHourlySummaries = summaries.length;
  for (let i = 0; i < lenHourlySummaries; i += 1) {
    const hourlySummary = summaries[i];
    const { start: startHs, stop: stopHs } = hourlySummary;
    let sizeData = 0;
    let readPosition = 0;
    sizeData = getSizeData(
      Math.min(stopHs, stopEvent),
      Math.max(startHs, startEvent),
      samplePerMsSecond,
    );
    readPosition =
      i === 0 ? getSizeData(startEvent, Math.min(startHs, startEvent), samplePerMsSecond) : 0;
    hourlySummary.dataRange = {
      start: Math.ceil(readPosition),
      stop: Math.floor(readPosition + sizeData),
    };
  }
};

export const filterHourlySummaryRanges = ({
  summaries,
  start,
  stop,
  samplingFrequency,
  channels,
  willAddDataRanges,
}: {
  summaries: { data: any[] };
  start: number;
  stop: number;
  samplingFrequency?: number;
  channels?: any[];
  willAddDataRanges?: boolean;
}) => {
  const ranges = summaries.data.filter((summary) =>
    _.every([+summary.stop > +start, +summary.start < +stop]),
  );
  // Add data range to download byte from url with range
  if (willAddDataRanges) {
    addDataRanges({
      summaries: ranges,
      start,
      stop,
      samplingFrequency,
      channels,
    });
  }

  return ranges;
};

/**
 * Computes how many minutely heart-rate slots a time window should hold. Takes the whole-minute difference and adds one when the stop lands part way through a minute.
 *
 * @param startMoment - Start of the window.
 * @param stopMoment - Stop of the window, whose seconds decide the trailing slot.
 * @returns The expected number of minutely heart-rate entries.
 */
const getExpectedMinutelyHrLength = (startMoment: moment.Moment, stopMoment: moment.Moment) => {
  const diffMin = stopMoment.diff(startMoment, 'minutes');
  return diffMin + (stopMoment.second() > 0 ? 1 : 0);
};

export const createMinutelyHrChartData = ({
  hourlySummaries,
  minutelyHrs,
  start,
  stop,
  maxMinutelyHrs,
  minMinutelyHrs,
  isEditReportPage = false,
}: {
  hourlySummaries: any[];
  minutelyHrs: any[];
  start: moment.Moment;
  stop: moment.Moment;
  maxMinutelyHrs?: any[];
  minMinutelyHrs?: any[];
  isEditReportPage?: boolean;
}) => {
  const expectedLength = getExpectedMinutelyHrLength(start, stop);
  const hrResult: (number | null)[] = isEditReportPage
    ? _.times(expectedLength, (): null => null)
    : _.times(expectedLength, (): number => -1);

  const maxHrResult: number[] = _.times(expectedLength, (): number => -1);
  const minHrResult: number[] = _.times(expectedLength, (): number => -1);

  if (minutelyHrs.length > 0) {
    for (let i = 0; i < hourlySummaries.length; i += 1) {
      const hourlySummary = hourlySummaries[i];
      // *: Round start date
      const hourlySummaryStartMoment = moment(hourlySummary.start).startOf('minute');
      const hrs = minutelyHrs[i];
      const maxHrs = maxMinutelyHrs?.[i];
      const minHrs = minMinutelyHrs?.[i];
      if (hrs) {
        const startDiff = start.diff(hourlySummaryStartMoment, 'minutes');
        const resultIdx = startDiff >= 0 ? 0 : -startDiff;
        const hrIdx = startDiff >= 0 ? startDiff : 0;
        let currentIdx = resultIdx;
        for (let ii = hrIdx; ii < hrs.length && currentIdx < expectedLength; ii += 1) {
          hrResult[currentIdx] = Number(hrs[ii]);
          if (maxHrs) {
            maxHrResult[currentIdx] = Number(maxHrs[ii]);
          }
          if (minHrs) {
            minHrResult[currentIdx] = Number(minHrs[ii]);
          }
          currentIdx += 1;
        }
      }
    }
  }

  if (maxMinutelyHrs || minMinutelyHrs) {
    return [hrResult, maxHrResult, minHrResult];
  }

  return hrResult;
};

export const createChartData = ({
  hrArray,
  eventIndicators,
  firstEpoch,
  maxHr,
  maxHrArray,
  minHrArray,
  isEditReportPage = false,
}: {
  hrArray: number[];
  eventIndicators: any[];
  firstEpoch: number;
  maxHr: number;
  maxHrArray?: number[];
  minHrArray?: number[];
  isEditReportPage?: boolean;
}) => {
  const unsaveArtifactEvents: any[] = [];
  const unsaveAfibEvents: any[] = [];
  const unsaveOtherEvents: any[] = [];
  const artifactEventColumnIndexes: number[] = [];
  const artifactEventAreaIndexes: number[] = [];
  const afibEventColumnIndexes: number[] = [];
  const afibEventAreaIndexes: number[] = [];
  const otherEventColumnIndexes: number[] = [];
  const otherEventAreaIndexes: number[] = [];
  eventIndicators.forEach((eventIndicator) => {
    const { startIndex, stopIndex, isChanged, isNew } = eventIndicator;
    switch (eventIndicator.type) {
      case TYPE_RHYTHM_EVENT_ENUM.ARTIFACT: {
        if (isChanged || isNew) {
          unsaveArtifactEvents.push(eventIndicator);
        }
        if (startIndex === stopIndex) {
          artifactEventColumnIndexes.push(startIndex);
        } else {
          for (let i = startIndex; i <= stopIndex; i += 1) {
            artifactEventAreaIndexes.push(i);
          }
        }
        break;
      }
      case TYPE_RHYTHM_EVENT_ENUM.AFIB: {
        if (isChanged || isNew) {
          unsaveAfibEvents.push(eventIndicator);
        }
        if (startIndex === stopIndex) {
          afibEventColumnIndexes.push(startIndex);
        } else {
          for (let i = startIndex; i <= stopIndex; i += 1) {
            afibEventAreaIndexes.push(i);
          }
        }
        break;
      }
      case TYPE_BEAT_ENUM.DELETED: {
        //* ignore deleted event
        break;
      }
      default: {
        if (isChanged || isNew) {
          unsaveOtherEvents.push(eventIndicator);
        }
        if (startIndex === stopIndex) {
          otherEventColumnIndexes.push(startIndex);
        } else {
          for (let i = startIndex; i <= stopIndex; i += 1) {
            otherEventAreaIndexes.push(i);
          }
        }
        break;
      }
    }
  });

  const hrs: any[] = [];
  const guides: any[] = [];
  const artifactEventGuides: any[] = [];
  const afibEventGuides: any[] = [];
  const otherEventGuides: any[] = [];
  const missingPoints: any[] = [];
  const hrIndexes: any[] = [];
  const artifactEventAreaPoints: any[] = [];
  const afibEventAreaPoints: any[] = [];
  const otherEventAreaPoints: any[] = [];
  _.range(1440).forEach((x) => {
    const epoch = firstEpoch + x * 60000;
    const hr = hrArray[x];

    if (isEditReportPage) {
      if (_.isNil(hr)) {
        hrs.push([epoch, null, null, null]);
        missingPoints.push([epoch, maxHr]);

        if (!_.isNil(hrArray[x - 1])) {
          // *: Previous point has HR value
          missingPoints.splice(x - 1, 1, [epoch - 60000, maxHr]);
        }
      } else {
        hrs.push([epoch, hr, maxHrArray?.[x], minHrArray?.[x]]);
        hrIndexes.push({
          index: x,
          category: epoch,
          maxHr: maxHrArray?.[x],
          minHr: minHrArray?.[x],
          avgHr: hr,
        });
        // missingPoints.push([epoch, null]);

        if (_.isNil(hrArray[x - 1])) {
          // *: Previous point do not have HR value so add current point to missingPoints array
          missingPoints.push([epoch, maxHr]);
        } else {
          missingPoints.push([epoch, null]);
        }
      }
    } else if (hr < 0) {
      hrs.push([epoch, null, null, null]);

      missingPoints.push([epoch, maxHr]);
      if (hrArray[x - 1] >= 0) {
        // *: Previous point has HR value
        missingPoints.splice(x - 1, 1, [epoch - 60000, maxHr]);
      }
    } else {
      hrIndexes.push({
        index: x,
        category: epoch,
        maxHr: maxHrArray?.[x],
        minHr: minHrArray?.[x],
        avgHr: hr,
      });
      hrs.push([epoch, hr, maxHrArray?.[x], minHrArray?.[x]]);
      missingPoints.push([epoch, null]);

      if (hrArray[x - 1] < 0) {
        // *: Previous point do not have HR value so add current point to missingPoints array
        missingPoints.push([epoch, maxHr]);
      } else {
        missingPoints.push([epoch, null]);
      }
    }

    guides.push([epoch, maxHr, false]);
    if (artifactEventAreaIndexes.includes(x)) {
      artifactEventAreaPoints.push([epoch, maxHr, hr]);
      artifactEventGuides.push([epoch, null]);
    } else if (artifactEventColumnIndexes.includes(x)) {
      artifactEventAreaPoints.push([epoch, null, hr]);
      artifactEventGuides.push([epoch, maxHr]);
    } else {
      artifactEventAreaPoints.push([epoch, null, hr]);
      artifactEventGuides.push([epoch, null]);
    }
    if (afibEventAreaIndexes.includes(x)) {
      afibEventAreaPoints.push([epoch, maxHr, hr]);
      afibEventGuides.push([epoch, null]);
    } else if (afibEventColumnIndexes.includes(x)) {
      afibEventAreaPoints.push([epoch, null, hr]);
      afibEventGuides.push([epoch, maxHr]);
    } else {
      afibEventAreaPoints.push([epoch, null, hr]);
      afibEventGuides.push([epoch, null]);
    }
    if (otherEventAreaIndexes.includes(x)) {
      otherEventAreaPoints.push([epoch, maxHr, hr]);
      otherEventGuides.push([epoch, null]);
    } else if (otherEventColumnIndexes.includes(x)) {
      otherEventAreaPoints.push([epoch, null, hr]);
      otherEventGuides.push([epoch, maxHr]);
    } else {
      otherEventAreaPoints.push([epoch, null, hr]);
      otherEventGuides.push([epoch, null]);
    }
  });

  return {
    hrs,
    guides,
    missingPoints,
    hrIndexes,
    artifactEventGuides,
    artifactEventAreaPoints,
    afibEventGuides,
    afibEventAreaPoints,
    otherEventGuides,
    otherEventAreaPoints,
    unsaveArtifactEvents: unsaveArtifactEvents.sort(
      (a: any, b: any) => a.startIndex - b.startIndex,
    ),
    unsaveAfibEvents: unsaveAfibEvents.sort((a: any, b: any) => a.startIndex - b.startIndex),
    unsaveOtherEvents: unsaveOtherEvents.sort((a: any, b: any) => a.startIndex - b.startIndex),
  };
};

/**
 * Screens out the missing heart-rate placeholder used across the charts. Requires the value to be neither null nor undefined and to sit above minus one.
 *
 * @param value - The heart rate to check.
 * @returns True when the heart rate is a real measurement.
 */
export const isValidHr = (value: any) => !_.isNil(value) && value > -1;

const thresholdMinHr = 25;
const thresholdMaxHr = 250;

/**
 * Converts the accepted heart-rate limits into RR interval bounds measured in samples. Divides sixty seconds of samples by the minimum and maximum heart-rate thresholds, so the slowest accepted rate yields the longest interval.
 *
 * @param samplingFrequency - Samples recorded per second.
 * @returns An object carrying minRrInterval and maxRrInterval in samples.
 */
export const getRrInterval = (samplingFrequency: number) => {
  const maxRrInterval = (60 * samplingFrequency) / thresholdMinHr;
  const minRrInterval = (60 * samplingFrequency) / thresholdMaxHr;
  return { minRrInterval, maxRrInterval };
};

/**
 * Derives the minimum, average and maximum heart rate of one minute from its beat positions. Yields minus one placeholders for fewer than four beats, and otherwise slides a four-beat window skipping any quadruple containing a Q beat, converts the mean RR interval to bpm, discards intervals outside the accepted bounds, and tracks the beat positions bracketing each extreme.
 *
 * @param beats - Beat sample positions within the minute.
 * @param hesBeatStatus - Beat type per beat, where 80 marks a Q beat that is skipped.
 * @param samplingFrequency - Samples recorded per second, defaulting to 250.
 * @returns An object carrying maxHr, minHr, avgHr and the beat position ranges of the extremes.
 */
export const calculateMinutelyHr = (
  beats: number[] = [],
  hesBeatStatus: number[] = [],
  samplingFrequency = 250,
) => {
  if (!beats.length || beats.length < 4) {
    return {
      maxHr: -1,
      maxHrPosRange: [] as number[],
      minHr: -1,
      minHrPosRange: [] as number[],
      avgHr: -1,
    };
  }
  let maxHr = Number.MIN_VALUE;
  let minHr = Number.MAX_VALUE;
  let sumValidHr = 0;
  let validHrCount = 0;
  let maxHrPosRange: number[] = [];
  let minHrPosRange: number[] = [];
  const { minRrInterval, maxRrInterval } = getRrInterval(samplingFrequency);
  for (let i = 3; i < beats.length; i += 1) {
    //* Beat Q dont use to count rr
    if (
      ![
        hesBeatStatus[i - 3],
        hesBeatStatus[i - 2],
        hesBeatStatus[i - 1],
        hesBeatStatus[i],
      ].includes(80)
    ) {
      const [beat1, beat2, beat3, beat4] = [beats[i - 3], beats[i - 2], beats[i - 1], beats[i]];
      const rrMean = _.mean([beat2 - beat1, beat3 - beat2, beat4 - beat3]);
      if (minRrInterval <= rrMean && rrMean <= maxRrInterval) {
        const hr = (60 * samplingFrequency) / rrMean;
        sumValidHr += hr;
        validHrCount += 1;
        if (hr > maxHr) {
          maxHr = hr;
          maxHrPosRange = [beat1, beat4];
        }
        if (hr < minHr) {
          minHr = hr;
          minHrPosRange = [beat1, beat4];
        }
      }
    }
  }
  return {
    maxHr: maxHr === Number.MIN_VALUE ? -1 : Math.round(maxHr),
    maxHrPosRange,
    minHr: minHr === Number.MAX_VALUE ? -1 : Math.round(minHr),
    minHrPosRange,
    avgHr: Math.round(sumValidHr / validHrCount) || -1,
  };
};
